import { useEffect, type PropsWithChildren } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'sonner';
import { ThemeProvider } from '@/components/theme-provider';
import { ErrorBoundary } from '@/components/error-boundary';
import { ConfirmDialogProvider } from '@/components/ConfirmDialog';
import { GlobalLoadingBar } from '@/components/GlobalLoadingBar';
import { useThemeStore } from '@/stores/theme';
import { renewIfDue, useSessionStore } from '@/stores/session';
import { TourProvider } from '@/providers/TourProvider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// Arranque de la sesión (pase + perfil, ver `@/stores/session`) y renovación
// al volver a la pestaña: si el timer no corrió (pestaña dormida, equipo
// suspendido), renueva antes del primer click en vez de esperar un 401.
const SessionBoot = (): null => {
  const boot = useSessionStore((s) => s.boot);

  useEffect(() => {
    void boot();
  }, [boot]);

  useEffect(() => {
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') renewIfDue();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return null;
};

const SonnerToaster = () => {
  // Sonner respeta el modo resolved del theme store (auto/light/dark).
  const resolved = useThemeStore((s) => s.resolved);
  return (
    <Toaster
      position="top-right"
      expand={false}
      richColors
      closeButton
      theme={resolved === 'dark' ? 'dark' : 'light'}
      toastOptions={{
        classNames: {
          toast: 'group bg-surface border border-border text-text shadow-lg',
          title: 'text-text font-medium',
          description: 'text-text-muted',
          actionButton: 'bg-primary-solid text-primary-solid-fg',
          cancelButton: 'bg-surface text-text border border-border',
        },
      }}
    />
  );
};

export const Providers = ({ children }: PropsWithChildren) => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <ConfirmDialogProvider>
            <TourProvider>
              <SessionBoot />
              <GlobalLoadingBar />
              {children}
              <SonnerToaster />
              <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
            </TourProvider>
          </ConfirmDialogProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};
